import { useEffect, useMemo, useRef, useState, type KeyboardEvent as KeyboardEventReact } from 'react';
import { useAuth } from '../core/auth';
import { podeNoApp, type AppCode, type Permission } from '../core/permissions';
import { useNavigate } from '../core/rotas';
import { supabase } from '../core/supabase';
import { Icone, type ComponenteDeIcone } from '../ui/icones';
import { DialogoDoTopo } from './dialogo-do-topo';

/**
 * A busca do topo (o `GlobalSearch` do YardHub): abre por "Buscar ⌘K", por
 * ⌘K/Ctrl+K e por "/", procura de verdade nos dados da organização ativa e
 * leva direto ao lugar. Antes o topo tinha um campo que recebia o texto e não
 * mandava para lugar nenhum.
 *
 * Dois tipos de resultado:
 *   - "Ir para": as seções dos produtos, casadas pelo nome aqui mesmo, sem
 *     consulta. Respondem desde a primeira letra.
 *   - Dados: negócios, contatos, empresas e sites do Bio, a partir de duas
 *     letras, cinco por grupo, só dos produtos que a pessoa pode abrir.
 *     A RLS já filtra por organização; o `org_id` vai junto para a consulta
 *     não depender só dela e usar o índice.
 */

type Resultado = { id: string; rotulo: string; meta?: string; para: string; icone: ComponenteDeIcone };
type Grupo = { nome: string; itens: Resultado[] };

type Secao = { rotulo: string; produto: string; para: string; icone: ComponenteDeIcone; perm?: Permission; app?: AppCode };
const SECOES: Secao[] = [
  { rotulo: 'Início', produto: 'Suite', para: '/home', icone: Icone.Painel },
  { rotulo: 'Painel do CRM', produto: 'CRM', para: '/crm#painel', icone: Icone.Painel, perm: 'crm.deal:read' },
  { rotulo: 'Funil de vendas', produto: 'CRM', para: '/crm#funil', icone: Icone.Funil, perm: 'crm.deal:read' },
  { rotulo: 'Lista de negócios', produto: 'CRM', para: '/crm#lista', icone: Icone.Pipeline, perm: 'crm.deal:read' },
  { rotulo: 'Contatos do CRM', produto: 'CRM', para: '/crm#contatos', icone: Icone.Contato, perm: 'crm.deal:read' },
  { rotulo: 'Empresas', produto: 'CRM', para: '/crm#empresas', icone: Icone.Empresa, perm: 'crm.deal:read' },
  { rotulo: 'Atividades', produto: 'CRM', para: '/crm#atividades', icone: Icone.Tarefa, perm: 'crm.deal:read' },
  { rotulo: 'Leads', produto: 'CRM', para: '/crm#leads', icone: Icone.LeadScoring, perm: 'crm.deal:read' },
  { rotulo: 'Relatórios do CRM', produto: 'CRM', para: '/crm#relatorios', icone: Icone.Relatorio, perm: 'crm.deal:read' },
  { rotulo: 'Automações do CRM', produto: 'CRM', para: '/crm#automacoes', icone: Icone.Automacao, perm: 'crm.deal:read' },
  { rotulo: 'Configuração do funil', produto: 'CRM', para: '/crm#config', icone: Icone.Engrenagem, perm: 'crm.pipeline:manage' },
  { rotulo: 'Painel do Marketing', produto: 'Marketing', para: '/mkt', icone: Icone.Painel, perm: 'mkt.contact:read' },
  { rotulo: 'Contatos do Marketing', produto: 'Marketing', para: '/mkt/contacts', icone: Icone.Contato, perm: 'mkt.contact:read' },
  { rotulo: 'Segmentos', produto: 'Marketing', para: '/mkt/segments', icone: Icone.Segmento, perm: 'mkt.contact:read' },
  { rotulo: 'Landing pages', produto: 'Marketing', para: '/mkt/landing-pages', icone: Icone.LandingPage, perm: 'mkt.landingpage:read' },
  { rotulo: 'Formulários', produto: 'Marketing', para: '/mkt/forms', icone: Icone.Formulario, perm: 'mkt.landingpage:read' },
  { rotulo: 'Pop-ups do Marketing', produto: 'Marketing', para: '/mkt/popups', icone: Icone.Balao, perm: 'mkt.landingpage:read' },
  { rotulo: 'Campanhas de e-mail', produto: 'Marketing', para: '/mkt/emails', icone: Icone.EMail, perm: 'mkt.email:send' },
  { rotulo: 'Automações do Marketing', produto: 'Marketing', para: '/mkt/automations', icone: Icone.Automacao, perm: 'mkt.automation:read' },
  { rotulo: 'Relatórios do Marketing', produto: 'Marketing', para: '/mkt/reports', icone: Icone.Relatorio, perm: 'mkt.report:read' },
  { rotulo: 'Caixa de atendimento', produto: 'Flow', para: '/flw/inbox', icone: Icone.Inbox, perm: 'flw.conversation:handle' },
  { rotulo: 'Canais', produto: 'Flow', para: '/flw/channels', icone: Icone.Whatsapp, perm: 'flw.channel:connect' },
  { rotulo: 'Chatbots', produto: 'Flow', para: '/flw/chatbots', icone: Icone.Robo, perm: 'flw.chatbot:manage' },
  { rotulo: 'Modelos de mensagem', produto: 'Flow', para: '/flw/templates', icone: Icone.RespostaRapida, perm: 'flw.campaign:manage' },
  { rotulo: 'Campanhas de WhatsApp', produto: 'Flow', para: '/flw/campaigns', icone: Icone.Disparo, perm: 'flw.campaign:manage' },
  { rotulo: 'Relatórios de atendimento', produto: 'Flow', para: '/flw/reports', icone: Icone.Relatorio, perm: 'flw.report:read' },
  { rotulo: 'Flow Pop-up', produto: 'Pop-up', para: '/pop', icone: Icone.Balao, perm: 'pop.report:read' },
  { rotulo: 'Sites do Bio', produto: 'Bio', para: '/bio', icone: Icone.LinkBio, perm: 'bio.site:read' },
  { rotulo: 'Suitemail', produto: 'Mail', para: '/mail', icone: Icone.Enviados, app: 'MAIL' },
  { rotulo: 'Perfil', produto: 'Configurações', para: '/settings/profile', icone: Icone.Usuario },
  { rotulo: 'Usuários', produto: 'Configurações', para: '/settings/members', icone: Icone.Participantes },
  { rotulo: 'Marca', produto: 'Configurações', para: '/settings/branding', icone: Icone.Paleta },
  { rotulo: 'Organização', produto: 'Configurações', para: '/settings/organization', icone: Icone.Empresa },
  { rotulo: 'Segurança', produto: 'Configurações', para: '/settings/security', icone: Icone.Escudo },
];

/** Sem acento e em minúscula, para "relatorio" achar "Relatórios". */
const normalizar = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('pt-BR');
/** O termo vai dentro de `ilike` e de `or(...)`: vírgula, parêntese, `%` e `*`
 *  mudariam o sentido da consulta, então saem antes. */
const limpar = (s: string) => s.replace(/[%,()*\\]/g, ' ').replace(/\s+/g, ' ').trim();

export function BuscaGlobal({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const { org, permissions, installedApps, adminPlataforma } = useAuth();
  const navigate = useNavigate();
  const [termo, setTermo] = useState('');
  const [dados, setDados] = useState<Grupo[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState('');
  const lista = useRef<HTMLDivElement>(null);

  // Cada abertura começa limpa: a busca anterior não fica esperando.
  useEffect(() => { if (aberto) { setTermo(''); setDados([]); setErro(''); } }, [aberto]);

  const pode = (p?: Permission) => !p || podeNoApp(permissions, installedApps, p);
  const temApp = (a?: AppCode) => !a || installedApps.includes(a);

  const atalhos = useMemo(() => {
    const t = normalizar(termo.trim());
    if (!t) return [];
    return SECOES
      .filter((s) => pode(s.perm) && temApp(s.app) && (normalizar(s.rotulo).includes(t) || normalizar(s.produto).includes(t)))
      .slice(0, 6)
      .map<Resultado>((s) => ({ id: s.para, rotulo: s.rotulo, meta: s.produto, para: s.para, icone: s.icone }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termo, permissions, installedApps]);

  useEffect(() => {
    const t = limpar(termo);
    if (!aberto || t.length < 2 || !supabase || !org?.id) { setDados([]); setBuscando(false); setErro(''); return; }
    let valendo = true;
    setBuscando(true);
    setErro('');
    const relogio = window.setTimeout(async () => {
      const sb = supabase!;
      const como = `%${t}%`;
      const consultas: Promise<Grupo | null>[] = [];
      if (pode('crm.deal:read')) {
        consultas.push((async () => {
          const { data, error } = await sb.from('deals').select('id,title,contact_name,status').eq('org_id', org.id).ilike('title', como).limit(5);
          if (error) throw error;
          return { nome: 'Negócios', itens: (data ?? []).map((d) => ({
            id: `d${d.id}`, rotulo: d.title as string, meta: [d.contact_name, d.status === 'won' ? 'ganho' : d.status === 'lost' ? 'perdido' : 'em aberto'].filter(Boolean).join(' · '),
            para: `/crm?negocio=${d.id}#funil`, icone: Icone.Negocio,
          })) };
        })());
        consultas.push((async () => {
          const { data, error } = await sb.from('companies').select('id,name,domain').eq('org_id', org.id).ilike('name', como).limit(5);
          if (error) throw error;
          return { nome: 'Empresas', itens: (data ?? []).map((e) => ({
            id: `e${e.id}`, rotulo: e.name as string, meta: (e.domain as string) || undefined, para: `/crm?empresa=${e.id}#empresas`, icone: Icone.Empresa,
          })) };
        })());
      }
      const verContatoNoMkt = pode('mkt.contact:read');
      if (verContatoNoMkt || pode('crm.deal:read')) {
        consultas.push((async () => {
          const { data, error } = await sb.from('contacts').select('id,name,email').eq('org_id', org.id).or(`name.ilike.${como},email.ilike.${como}`).limit(5);
          if (error) throw error;
          return { nome: 'Contatos', itens: (data ?? []).map((c) => ({
            id: `c${c.id}`, rotulo: (c.name as string) || (c.email as string), meta: (c.email as string) || undefined,
            para: verContatoNoMkt ? `/mkt/contacts/${c.id}` : `/crm?contato=${c.id}#contatos`, icone: Icone.Contato,
          })) };
        })());
      }
      if (pode('bio.site:read')) {
        consultas.push((async () => {
          const { data, error } = await sb.from('bio_sites').select('id,name,handle').eq('org_id', org.id).or(`name.ilike.${como},handle.ilike.${como}`).limit(5);
          if (error) throw error;
          return { nome: 'Sites do Bio', itens: (data ?? []).map((s) => ({
            id: `s${s.id}`, rotulo: (s.name as string) || (s.handle as string), meta: s.handle ? `myard.bio/${s.handle}` : undefined, para: `/bio/sites/${s.id}`, icone: Icone.LinkBio,
          })) };
        })());
      }
      // Um grupo que falha (tabela de um produto não migrado) não derruba os
      // outros: o erro só aparece se TODOS falharem.
      const respostas = await Promise.allSettled(consultas);
      if (!valendo) return;
      const ok = respostas.flatMap((r) => (r.status === 'fulfilled' && r.value ? [r.value] : []));
      setDados(ok.filter((g) => g.itens.length > 0));
      setErro(consultas.length > 0 && ok.length === 0 ? 'A busca não respondeu. Tente de novo em instantes.' : '');
      setBuscando(false);
    }, 260);
    return () => { valendo = false; window.clearTimeout(relogio); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termo, aberto, org?.id, permissions, installedApps]);

  const grupos: Grupo[] = [...(atalhos.length ? [{ nome: 'Ir para', itens: atalhos }] : []), ...dados];
  const temAlgo = grupos.some((g) => g.itens.length > 0);
  const curto = limpar(termo).length < 2;

  function ir(r: Resultado) {
    aoFechar();
    navigate(r.para);
  }

  /** Setas andam entre os resultados; do campo, a seta para baixo entra na lista. */
  function navegarComSetas(e: KeyboardEventReact<HTMLElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const botoes = Array.from(lista.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
    if (botoes.length === 0) return;
    e.preventDefault();
    const atual = botoes.indexOf(document.activeElement as HTMLButtonElement);
    const proximo = atual < 0 ? (e.key === 'ArrowDown' ? 0 : botoes.length - 1)
      : (atual + (e.key === 'ArrowDown' ? 1 : -1) + botoes.length) % botoes.length;
    botoes[proximo]?.focus();
  }

  return (
    <DialogoDoTopo
      aberto={aberto}
      aoFechar={aoFechar}
      olho="Busca"
      titulo="O que vamos encontrar?"
      frase={org ? `Negócios, contatos, empresas e telas de ${org.name}.` : adminPlataforma ? 'As telas do console da Yard.' : undefined}
      className="busca-global"
    >
      <div className="busca-global__campo">
        {buscando ? <Icone.Carregando tamanho={16} className="busca-global__gira" aria-hidden="true" /> : <Icone.Busca tamanho={16} aria-hidden="true" />}
        <input
          type="search"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const primeiro = grupos.find((g) => g.itens.length)?.itens[0];
              if (primeiro) { e.preventDefault(); ir(primeiro); }
              return;
            }
            navegarComSetas(e);
          }}
          placeholder="Nome do negócio, do contato, da empresa ou de uma tela"
          aria-label="Buscar no Suite"
          aria-controls="busca-global-resultados"
          autoComplete="off"
        />
      </div>
      <div id="busca-global-resultados" ref={lista} className="busca-global__resultados" aria-busy={buscando} onKeyDown={navegarComSetas}>
        {erro ? (
          <p className="busca-global__aviso" role="alert">{erro}</p>
        ) : !termo.trim() ? (
          <p className="busca-global__aviso">Digite para buscar. Duas letras já procuram nos negócios, contatos e empresas.</p>
        ) : !temAlgo && !buscando ? (
          <p className="busca-global__aviso" role="status">
            {curto ? 'Mais uma letra para buscar nos dados.' : `Nada encontrado para "${termo.trim()}".`}
          </p>
        ) : (
          grupos.map((g) => (
            <section key={g.nome} aria-label={g.nome}>
              <h4>{g.nome}</h4>
              {g.itens.map((r) => {
                const Desenho = r.icone;
                return (
                  <button type="button" key={r.id} className="busca-global__item" onClick={() => ir(r)}>
                    <span className="busca-global__ic" aria-hidden="true"><Desenho tamanho={15} /></span>
                    <span className="busca-global__txt"><b>{r.rotulo}</b>{r.meta && <small>{r.meta}</small>}</span>
                    <Icone.SetaDireita tamanho={14} aria-hidden="true" />
                  </button>
                );
              })}
            </section>
          ))
        )}
      </div>
    </DialogoDoTopo>
  );
}
